import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { CommentaryAccessNotice, isAccessError } from "@/components/commentary/CommentaryAccess";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ChapterIntro } from "@/components/commentary/CommentaryPanel";
import { CommentaryBreadcrumb } from "@/components/commentary/CommentaryBreadcrumb";

function BookNav({ slug, prev, next, bottom = false }) {
  const cls = "inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-serif text-sm text-muted-foreground transition-colors hover:border-gold hover:text-foreground";
  return (
    <div className={`flex items-center justify-between gap-2 ${bottom ? "mt-10 border-t border-border pt-6" : ""}`} data-testid={`commentary-book-nav${bottom ? "-bottom" : ""}`}>
      {prev ? <Link to={`/commentary/${slug}/${encodeURIComponent(prev)}`} className={cls} data-testid={`commentary-prev-book${bottom ? "-bottom" : ""}`}><ChevronLeft className="h-4 w-4" /> {prev}</Link> : <span />}
      {next ? <Link to={`/commentary/${slug}/${encodeURIComponent(next)}`} className={cls} data-testid={`commentary-next-book${bottom ? "-bottom" : ""}`}>{next} <ChevronRight className="h-4 w-4" /></Link> : <span />}
    </div>
  );
}

/* /commentary/:slug/:book — book introduction + chapter grid */
export default function CommentaryBook() {
  const { slug, book } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["commentary-book", slug, book], queryFn: async () => (await api.get(`/commentaries/${slug}/${encodeURIComponent(book)}`)).data, staleTime: 60 * 60 * 1000, retry: (n, e) => !isAccessError(e) && n < 2 });
  const chapterTitles = useMemo(() => Object.fromEntries((data?.chapters || []).map((c) => [c.chapter, c.sections.map((s) => s.title).filter(Boolean).join(" · ")])), [data]);
  if (error) return <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8"><CommentaryAccessNotice error={error} /></div>;
  if (isLoading || !data) return <div className="mx-auto max-w-4xl px-4 py-10"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="commentary-book">
      <Seo title={`${book} — ${data.commentary.name} Commentary`} description={data.intro[0]?.slice(0, 160)} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CommentaryBreadcrumb slug={slug} book={book} />
        <BookNav slug={slug} prev={data.prev_book} next={data.next_book} />
      </div>
      {data.commentary.display_name && <p className="mt-3 font-serif text-lg italic text-gold" data-testid="commentary-full-name">{data.commentary.display_name}</p>}
      <h1 className="mt-1 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl" data-testid="commentary-book-title">{book}</h1>
      <div className="mt-6"><ChapterIntro paragraphs={data.intro} label={`Introduction to ${book}`} defaultOpen testId="commentary-book-intro" /></div>
      <h2 className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Chapters</h2>
      <div className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-8 md:grid-cols-10">
        {data.chapters.map((c) => (
          <Link key={c.chapter} to={`/commentary/${slug}/${encodeURIComponent(book)}/${c.chapter}`} title={chapterTitles[c.chapter]}
            className="flex h-11 items-center justify-center rounded-xl border border-border bg-card font-mono text-sm text-foreground transition-colors hover:border-gold hover:bg-gold/10" data-testid={`commentary-chapter-${c.chapter}`}>
            {c.chapter}
          </Link>
        ))}
      </div>
      <BookNav slug={slug} prev={data.prev_book} next={data.next_book} bottom />
    </div>
  );
}
